import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { flatten, uniqBy } from "lodash";
import { useEffect, useEffectEvent, useMemo } from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";

import type {
  ChatRoom,
  ChatRoomResponseApi,
} from "@/react/components/Modals/MessagingModalCompass/LeftSidebar/List/ChatRoomTypes";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { UNIFIED_INBOX_MESSAGES_ROOT } from "@/react/hooks/chatsV2/messaging/useMessagingPaths";
import { useInfiniteQueryHelpers } from "@/react/hooks/reactQuery/useInfiniteQueryHelpers";

import { archivedChatRoomQueryKey } from "./chatRoomArchiveKeys";
import {
  onChatMarkAllAsRead,
  onChatRoomParticipantsUpdated,
  onChatRoomPinned,
  onChatRoomRead,
  onChatRoomUnpinned,
  onChatRoomUpdated,
} from "./eventHandlers";

const DROPDOWN_PER_PAGE = 10;
const MODAL_PER_PAGE = 20;
const QUERY_KEY = ["sidebar", "chat-rooms", "all"];
export const MESSAGING_MODAL_QUERY_KEY = ["messaging", "chat-rooms", "all"];
const UNREAD_CHAT_ROOMS_QUERY_KEY = ["sidebar", "chat-rooms", "unread"];

interface Props {
  openChatRoomUuid?: string | null;
  uuids?: Array<string>;
}

export const useChatsSidebarApi = ({ openChatRoomUuid, uuids }: Props) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentCommunitySettings } = usePunditUserContext();
  const {
    cached_unread_chat_room_ids_enabled: isCachedUnreadChatRoomIdsEnabled,
  } = currentCommunitySettings || {};
  const { updateAllRecords } = useInfiniteQueryHelpers();
  const pathname = useLocation({ select: location => location.pathname });
  const isMessagingRoute =
    pathname.startsWith("/messages") ||
    pathname.startsWith(UNIFIED_INBOX_MESSAGES_ROOT);
  const queryKey = useMemo(() => {
    if (uuids) {
      return UNREAD_CHAT_ROOMS_QUERY_KEY;
    }
    return isMessagingRoute ? MESSAGING_MODAL_QUERY_KEY : QUERY_KEY;
  }, [uuids, isMessagingRoute]);

  const updateQueryCache = (newData: any) => {
    updateAllRecords(newData, queryKey);
  };

  const fetchChatRooms = (page: any) => {
    const params: any = {
      page: page,
      per_page: isMessagingRoute ? MODAL_PER_PAGE : DROPDOWN_PER_PAGE,
    };
    if (uuids) {
      if (isCachedUnreadChatRoomIdsEnabled) {
        params["only_unread"] = true;
      } else {
        params["uuids"] = uuids;
      }
    }
    return reactQueryGet<ChatRoomResponseApi>(
      internalApi.chatRooms.index({
        params,
      }),
    );
  };

  const {
    isInitialLoading: isLoading,
    refetch: fetchUserChatRooms,
    data: { pages: chatRooms = [] } = {},
    hasNextPage = false,
    fetchNextPage,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey,
    queryFn: ({ pageParam = 1 }) => fetchChatRooms(pageParam),
    select: ({ pages = [] }) =>
      // RQ recommends to restructure your data that it still includes data.pages and data.pageParams properties - https://tanstack.com/query/v4/docs/react/guides/infinite-queries
      ({
        pages: uniqBy(flatten(pages.map(page => page.records)), "uuid"),
        pageParams: pages.map(page => page.page),
      }),
    getNextPageParam: lastPage =>
      lastPage.has_next_page ? Number(lastPage.page) + 1 : undefined,
  });

  const fetchChatRoomForUuid = (uuid: any) =>
    chatRooms.find(chatRoom => chatRoom.uuid === uuid);

  const updateChatRooms = (updatedChatRoom: ChatRoom) => {
    if (updatedChatRoom.pinned_at) {
      const updatedChatRooms = chatRooms.map(chatRoom => {
        if (chatRoom.uuid === updatedChatRoom.uuid) {
          return updatedChatRoom;
        }
        return chatRoom;
      });

      updateQueryCache(updatedChatRooms);
    } else {
      const pinnedChatRooms: ChatRoom[] = [];
      const remainingChatRooms: ChatRoom[] = [];
      chatRooms.forEach(chatRoom => {
        if (chatRoom.pinned_at) {
          pinnedChatRooms.push(chatRoom);
        } else if (chatRoom.uuid !== updatedChatRoom.uuid) {
          remainingChatRooms.push(chatRoom);
        }
      });

      updateQueryCache([
        ...pinnedChatRooms,
        updatedChatRoom,
        ...remainingChatRooms,
      ]);
    }
  };

  const appendChatRoom = (chatRoomUuid: any) => {
    const target = pathname.startsWith(UNIFIED_INBOX_MESSAGES_ROOT)
      ? UNIFIED_INBOX_MESSAGES_ROOT
      : "/messages";
    // A room known to be archived (e.g. returning to a last-opened or stale URL)
    // redirects away immediately — no fetch round-trip, so the archived room
    // never flashes on screen before redirecting.
    if (queryClient.getQueryData(archivedChatRoomQueryKey(chatRoomUuid))) {
      void navigate({ to: target as any });
      return;
    }
    // Otherwise fetch to learn whether it is archived (e.g. a deep link to a room
    // not in the sidebar whose state we don't yet know locally).
    void reactQueryGet(internalApi.chatRooms.show({ uuid: chatRoomUuid })).then(
      (data: any) => {
        const room = data.chat_room;
        if (room?.current_participant?.archived) {
          void navigate({ to: target as any });
        } else if (room) {
          updateChatRooms(room);
        }
      },
    );
  };

  const appendOpenChatRoomIfNotPresent = (openChatRoomUuid: any) => {
    if (openChatRoomUuid && openChatRoomUuid !== "new") {
      if (!chatRooms.some(({ uuid }) => uuid === openChatRoomUuid)) {
        appendChatRoom(openChatRoomUuid);
      }
    }
  };

  const handleOpenChatRoom = useEffectEvent((uuid?: string | null) => {
    appendOpenChatRoomIfNotPresent(uuid);
  });

  useEffect(() => {
    if (chatRooms.length > 0) {
      handleOpenChatRoom(openChatRoomUuid);
    }
  }, [chatRooms, openChatRoomUuid]);

  const onNewMessage = (eventData: any, communityMemberId: any) => {
    const latestChatRoomMessage = eventData.json_message;
    const {
      chat_room_uuid,
      sender: { community_member_id },
    } = latestChatRoomMessage;
    const messageChatRoom = fetchChatRoomForUuid(chat_room_uuid);

    if (messageChatRoom) {
      const { isOpen } = messageChatRoom;

      //community_member_id: sender of the message
      //communityMemberId: current logged in community member
      const hasUnreadMessagesAfterNewMessage =
        !isOpen && community_member_id !== communityMemberId;

      const updatedChatRoom = {
        ...messageChatRoom,
        last_message: latestChatRoomMessage,
        has_unread_messages: hasUnreadMessagesAfterNewMessage,
      };

      updateChatRooms(updatedChatRoom);
    } else {
      // The message is received in the chat room which is not present in the chatRooms state.
      // Refetch all chat rooms information from backend.
      void queryClient.invalidateQueries({ queryKey });
    }
  };

  const onChatRoomParticipantDestroy = (eventData: any) => {
    const { chat_room_uuid } = eventData;
    const updatedChatRooms = chatRooms.filter(
      chatRoom => chatRoom.uuid !== chat_room_uuid,
    );
    updateQueryCache(updatedChatRooms);
    if (openChatRoomUuid === chat_room_uuid) {
      const target = pathname.startsWith(UNIFIED_INBOX_MESSAGES_ROOT)
        ? UNIFIED_INBOX_MESSAGES_ROOT
        : "/messages";
      void navigate({ to: target as any });
    }
  };

  const onChatRoomCreated = () => {
    void queryClient.invalidateQueries({ queryKey });
  };

  const onEventReceive = (eventData: any, communityMemberId: any) => {
    switch (eventData.event) {
      case "newMessage":
        onNewMessage(eventData, communityMemberId);
        break;
      case "chatRoomRead":
        onChatRoomRead(eventData, chatRooms, updateQueryCache);
        break;
      case "chatMarkAllAsRead":
        onChatMarkAllAsRead(eventData, chatRooms, updateQueryCache);
        break;
      case "chatRoomCreated":
        onChatRoomCreated();
        break;
      case "chatRoomUpdated":
        onChatRoomUpdated(eventData, chatRooms, updateQueryCache);
        break;
      case "chatRoomDeleted":
        onChatRoomParticipantDestroy(eventData);
        break;
      case "chatRoomParticipantsUpdated":
        onChatRoomParticipantsUpdated(eventData, chatRooms, updateQueryCache);
        break;
      case "chatRoomParticipantDestroy":
        onChatRoomParticipantDestroy(eventData);
        break;
      case "chatRoomPinned":
        onChatRoomPinned(eventData, chatRooms, updateQueryCache);
        break;
      case "chatRoomUnPinned":
        onChatRoomUnpinned(
          eventData,
          chatRooms,
          updateQueryCache,
          fetchUserChatRooms,
        );
        break;
    }
  };

  return {
    chatRooms,
    setChatRooms: updateQueryCache,
    fetchChatRoomForUuid,
    onEventReceive,
    chatRoomsHasNextPage: hasNextPage,
    hasNextPage,
    fetchNextPage,
    fetchUserChatRooms,
    isLoading,
  };
};
